import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import CustomTabs from "components/Shared/CustomTabs";
import CustomTabPanel from "components/Shared/CustomTabPanel";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import { useVendorProfileDetailContext } from "contexts/VendorProfileDetailContext";
import VendorShipmentList from "./VendorShipmentList";

import { useAuth } from "hooks";
import {
  useLazyListVendorShipmentsQuery,
  useLazyListVendorLedgerQuery,
  useLazyListVendorPricingsQuery,
} from "store/services/vendorProfileApi";
import { useGetDocumentsBySourceQuery } from "store/services/documentApi";
import VendorPriceList from "./VendorPriceList";
import LimitsTab from "screens/App/CRM/Contact/VendorProfile/tabs/LimitsTab";
import VendorTicketList from "./VendorTicketList";
import { useLazyGetTicketListForVendorProfileQuery } from "store/services/ticketApi";
import ServiceBranchesList from "screens/App/CRM/Contact/VendorProfile/VendorServiceBranch/ServiceBranchesList ";
import VendorDocumentTab from "screens/App/CRM/Document/VendorDocumentTab";
import VendorBankDetailsTab from "screens/App/CRM/Contact/VendorProfile/tabs/BankDetailsTab";
import ShippingRateTab from "screens/App/CRM/Contact/VendorProfile/VendorShippingRate/ShippingRateTab";
import VendorCodPaymentTab from "./VendorCodPaymentTab ";
import VendorProfileQuotationHistoryScreen from "./VendorProfileQuotationHistoryScreen";
import { useLazyGetVendorProfileQuotationsByProfileCountQuery } from "store/services/vendorQuotationApi";

const VendorProfileDetailTab = () => {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "shipment";
  const { isCustomer, isVendor, isSystemAdmin, isAccountAdmin } = useAuth();

  const { vendorProfileDetail, loadingVendorProfileDetail } =
    useVendorProfileDetailContext();

  const [fetchShipments, { data: shipmentRes, isLoading: isLoadingShipments, isFetching: isFetchingShipments }] =
    useLazyListVendorShipmentsQuery();

  const [fetchLedger, { isLoading: isLoadingLedger, isFetching: isFetchingLedger }] =
    useLazyListVendorLedgerQuery();


  const [fetchPricing, { data: pricingRes }] =
    useLazyListVendorPricingsQuery();

  const [fetchTickets, { data: ticketRes, isLoading: isLoadingTickets, isFetching: isFetchingTickets }] =
    useLazyGetTicketListForVendorProfileQuery();

  const [fetchQuotations, { data: quotationRes }] =
    useLazyGetVendorProfileQuotationsByProfileCountQuery();

  // Fetch documents to get the count for the badge
  const { data: documents, isLoading: isLoadingDocuments, isFetching: isFetchingDocuments } = useGetDocumentsBySourceQuery(
    { source: "VendorProfile", sourceRef: vendorProfileDetail?._id },
    { skip: !vendorProfileDetail?._id }
  );

  const filteredDocuments = Array.isArray(documents)
    ? documents.filter((doc) => (isCustomer || isVendor ? doc.showClient : true))
    : [];

  useEffect(() => {
    if (!vendorProfileDetail?._id) return;

    fetchShipments({
      vendorId: vendorProfileDetail._id,
      page: 1,
      limit: 1,
    });

    fetchPricing({
      vendorId: vendorProfileDetail._id,
      page: 1,
      limit: 1,
    });

    fetchLedger({
      vendorId: vendorProfileDetail._id,
      page: 1,
      limit: 1,
    });

    fetchQuotations({
      vendorProfileId: vendorProfileDetail._id,
      page: 1,
      limit: 1,
    });



    fetchTickets({
      vendorId: vendorProfileDetail._id,
      active: true,
      page: 1,
      limit: 1,
    });
  }, [vendorProfileDetail?._id, fetchShipments, fetchPricing, fetchLedger, fetchTickets, fetchQuotations]);

  const bankAccountsCount = React.useMemo(() => {
    const seen = new Set();
    let count = 0;
    if (Array.isArray(vendorProfileDetail?.bankAccount)) {
      vendorProfileDetail.bankAccount.forEach((item) => {
        const accNo = item?.bankAccountNumber != null ? String(item.bankAccountNumber) : "";
        if (item?.bankName && !seen.has(accNo)) {
          seen.add(accNo);
          count++;
        }
      });
    }
    if (vendorProfileDetail?.vendorProfileBankDetail?.bankName) {
      const accNo = vendorProfileDetail.vendorProfileBankDetail.bankAccountNumber != null
        ? String(vendorProfileDetail.vendorProfileBankDetail.bankAccountNumber)
        : "";
      if (!seen.has(accNo)) {
        seen.add(accNo);
        count++;
      }
    }
    return count;
  }, [vendorProfileDetail]);

  const tabs = [
    {
      label: "Shipment",
      value: "shipment",
      badgeCount: shipmentRes?.totalItems || 0,
    },
    {
      label: "Pricing",
      value: "price",
      badgeCount: pricingRes?.totalItems || 0,
    },
    {
      label: "Bank Details",
      value: "bankDetails",
      badgeCount: bankAccountsCount,
    },
    {
      label: "Payment",
      value: "payment",
    },
    {
      label: "Quotation",
      value: "quotation",
      badgeCount: quotationRes?.totalSize || 0,
    },
    {
      label: "Document",
      value: "document",
      badgeCount: filteredDocuments.length || documents?.totalItems || 0,
    },
    {
      label: "Limits",
      value: "limits",
      badgeCount: vendorProfileDetail?.vendorLimits ? "VL" : "No",
    },
    {
      label: "Tickets",
      value: "ticket",
      badgeCount: ticketRes?.totalItems || 0,
    },
    {
      label: "Service Branches",
      value: "service-branches",
    },
    ...(isSystemAdmin || isAccountAdmin
      ? [
        {
          label: "Shipping Rate",
          value: "shipping-rate",
          badgeCount: vendorProfileDetail?.shippingRate?.length || 0,
        },
      ]
      : []),
  ];

  // Ensure activeTab is valid
  const validTabs = tabs.map((tab) => tab.value);

  // Check if activeTab is valid, or if it's a ticket status that should show the ticket tab
  const ticketStatuses = ["Open", "In Progress", "Closed", "All", "Inactive"];
  const validatedActiveTab = validTabs.includes(activeTab)
    ? activeTab
    : ticketStatuses.includes(activeTab)
      ? "ticket"
      : "shipment";

  return (
    <>
      <CustomTabs tabs={tabs} activeTab={validatedActiveTab} />
      {loadingVendorProfileDetail ||
        !vendorProfileDetail ||
        isLoadingShipments ||
        isFetchingShipments ||
        isLoadingLedger ||
        isFetchingLedger ||
        isLoadingDocuments ||
        isFetchingDocuments ||
        isLoadingTickets ||
        isFetchingTickets ? (
        <RowsSkeleton />
      ) : (
        <>
          <CustomTabPanel activeTab={validatedActiveTab} value="shipment">
            <VendorShipmentList />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="price">
            <VendorPriceList />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="bankDetails">
            <VendorBankDetailsTab vendorId={vendorProfileDetail?._id} />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="payment">
            <VendorCodPaymentTab vendorId={vendorProfileDetail?._id} />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="quotation">
            <VendorProfileQuotationHistoryScreen vendorProfileId={vendorProfileDetail?._id} />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="document">
            <VendorDocumentTab source="VendorProfile" sourceRef={vendorProfileDetail?._id} />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="ticket">
            <VendorTicketList vendorId={vendorProfileDetail?._id} />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="limits">
            <LimitsTab vendorId={vendorProfileDetail?._id} />
          </CustomTabPanel>
          <CustomTabPanel activeTab={activeTab} value="service-branches">
            <ServiceBranchesList />
          </CustomTabPanel>
          <CustomTabPanel activeTab={activeTab} value="shipping-rate">
            <ShippingRateTab vendorId={vendorProfileDetail?._id} />
          </CustomTabPanel>
        </>
      )}
    </>
  );
};

export default VendorProfileDetailTab;